import React from "react";
import styled from "styled-components";
import { Link } from "@inertiajs/react";
import { brand_orange_deep } from "../../constants/styles";
import PagedCarousel from "../Carousel";
import SectionShell, {
  SectionTitle,
  SectionSubtitle,
  SectionGroup,
} from "../SectionShell";
import usePagedSection from "../usePagedSection";
import CardActionsOverlay from "../CardActionsOverlay";
import { Skeleton } from "../Skeleton";
import { apiGet } from "../../api";

// One card style for all groups. When `coming_soon` is true on the parent
// group, we swap the gradient + overlay — individual curriculums inherit.
const Card = styled.article`
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition:
    transform 200ms ease,
    filter 200ms ease;

  &:hover {
    transform: translateY(-4px);
  }
`;

const Thumbnail = styled.div`
  aspect-ratio: 16/10;
  border-radius: 12px;
  background: ${({ $isComingSoon }) =>
    $isComingSoon
      ? "radial-gradient(ellipse at 30% 30%, rgba(255,255,255,0.3), transparent 55%), linear-gradient(135deg, #fbbf24, #f59e0b)"
      : "radial-gradient(ellipse at 30% 30%, rgba(255,255,255,0.6), transparent 55%), linear-gradient(135deg, #e5e7eb, #cbd5e1)"};
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  position: relative;
  transition: box-shadow 220ms ease;

  [data-card-host]:hover & {
    box-shadow: 0 14px 30px rgba(0, 0, 0, 0.12);
  }

  ${({ $isComingSoon }) =>
    $isComingSoon &&
    `
    &::after {
      content: "COMING SOON";
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ffffff;
      font-weight: 800;
      font-size: 16px;
      letter-spacing: 0.15em;
      text-shadow: 0 1px 2px rgba(0,0,0,0.2);
      z-index: 1;
    }
  `}
`;

const ThumbnailImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

// YouTube-style progress bar pinned to the bottom of the thumbnail. Rendered
// only when completion_percentage > 0 (per product spec — zero-progress
// curriculums should look the same as anonymous ones). A 3px bar with a soft
// backing is legible over every thumbnail variant (gradient + image).
const ProgressTrack = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background: rgba(0, 0, 0, 0.18);
  z-index: 2;
  pointer-events: none;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => `${Math.max(0, Math.min(100, $pct))}%`};
  background: ${brand_orange_deep};
  transition: width 400ms ease;
`;

const CardTitle = styled.div`
  color: ${brand_orange_deep};
  font-weight: 700;
  font-size: 16px;
`;

const CardSubtitle = styled.div`
  color: #6b7280;
  font-size: 12px;
  line-height: 1.5;

  [data-theme="dark"] & {
    color: #d1d5db;
  }
`;

// Thumbnail + title + subtitle skeleton; shared by Sertifikasi, Rekaman,
// and Talksamtalk since they all render the same card shape.
function renderThumbnailCardSkeleton(i) {
  return (
    <Card key={`sk-${i}`} aria-hidden="true">
      <Skeleton $radius="12px" style={{ aspectRatio: "16 / 10" }} />
      <Skeleton $width="55%" $height="16px" $radius="4px" />
      <Skeleton $width="85%" $height="12px" $radius="4px" />
    </Card>
  );
}

function buildCardRenderer(isComingSoon) {
  return function renderCertCard(c) {
    // Server only emits these when a user is signed in (see CertificationsController).
    // For anonymous visitors they're `undefined` and the overlay falls back to
    // its default `false` props; the progress bar doesn't render.
    const pct = Number(c.completion_percentage) || 0;
    return (
      <Card key={c.id}>
        <Thumbnail $isComingSoon={isComingSoon} data-card-host tabIndex={0}>
          {c.image_url && !isComingSoon && (
            <ThumbnailImage
              src={c.image_url}
              alt={c.title || ""}
              loading="lazy"
            />
          )}
          {!isComingSoon && (
            <CardActionsOverlay
              resourceType={c.resource_type || "Curriculum"}
              resourceId={c.resource_id || c.id}
              detailLink={c.detail_link}
              initialLiked={!!c.is_liked}
              initialDisliked={!!c.is_disliked}
              initialBookmarked={!!c.is_bookmarked}
            />
          )}
          {!isComingSoon && pct > 0 && (
            <ProgressTrack
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Progres modul"
            >
              <ProgressFill $pct={pct} />
            </ProgressTrack>
          )}
        </Thumbnail>
        <CardTitle>{c.title}</CardTitle>
        <CardSubtitle>{c.subtitle}</CardSubtitle>
      </Card>
    );
  };
}

function CertGroup({
  title,
  courseUrl,
  items,
  comingSoon,
  ariaLabel,
  loading: loadingOverride,
}) {
  // Pre-loaded path: usePagedSection slices the array client-side and
  // handles the narrow ↔ wide perPage responsive behavior.
  const {
    items: pageItems,
    pageIdx,
    setPageIdx,
    totalPages,
    loading,
    perPage,
  } = usePagedSection({ sampleItems: items });

  const renderItem = React.useMemo(
    () => buildCardRenderer(!!comingSoon),
    [comingSoon],
  );

  // When the parent course has a slug AND the rebrand routes are wired,
  // the group title becomes an Inertia <Link> into the course detail page.
  // Falls back to a plain string for "Memuat..." skeletons and for groups
  // emitted before the slug backfill ran.
  const titleNode =
    courseUrl && !comingSoon && !loadingOverride ? (
      <GroupTitleLink href={courseUrl}>{title}</GroupTitleLink>
    ) : (
      title
    );

  return (
    <SectionGroup title={titleNode}>
      <PagedCarousel
        items={pageItems}
        perPage={perPage}
        pageIdx={pageIdx}
        totalPages={totalPages}
        onPageChange={setPageIdx}
        loading={loadingOverride || loading}
        renderItem={renderItem}
        renderSkeleton={renderThumbnailCardSkeleton}
        ariaLabel={ariaLabel}
        emptyMessage="Belum ada materi di kategori ini."
      />
    </SectionGroup>
  );
}

const GroupTitleLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  border-bottom: 2px solid transparent;
  transition:
    color 150ms ease,
    border-color 150ms ease;

  &:hover {
    color: ${brand_orange_deep};
    border-bottom-color: ${brand_orange_deep};
  }

  &:focus-visible {
    outline: 2px solid ${brand_orange_deep};
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

// Placeholder group shown while the /certifications endpoint is in flight.
// Delegates to the real CertGroup with sampleItems=[] so it borrows the
// exact chrome (GroupTitle bar, responsive perPage, card grid) and shows
// the thumbnail-card skeletons. Title is a short placeholder string to
// avoid nesting a block-level Skeleton <div> inside an <h3>.
function SertifikasiGroupSkeleton() {
  return (
    <CertGroup
      title="Memuat..."
      items={[]}
      loading
      ariaLabel="Memuat sertifikasi"
    />
  );
}

const SKELETON_GROUP_KEYS = ["skeleton-group-1", "skeleton-group-2"];

function SertifikasiCarousel() {
  const [groups, setGroups] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    apiGet("/api/v1/homepage/certifications")
      .then(({ parsedResponse }) => {
        if (cancelled) return;
        setGroups(
          Array.isArray(parsedResponse?.data) ? parsedResponse.data : [],
        );
      })
      .catch((err) => {
        if (typeof console !== "undefined") {
          console.warn("[SertifikasiCarousel] fetch failed", err);
        }
        if (!cancelled) setGroups([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <SectionShell id="sertifikasi">
      <SectionTitle>Sertifikasi</SectionTitle>
      <SectionSubtitle>
        Simak Seluruh Video dan Ikuti Ujian di Setiap Topiknya Untuk Mendapatkan
        Sertifikat yang Kamu Butuhkan!
      </SectionSubtitle>
      {loading
        ? // Two placeholder groups — enough to telegraph "multiple sections
          // will appear here" without claiming a specific final count.
          SKELETON_GROUP_KEYS.map((skeletonKey) => (
            <SertifikasiGroupSkeleton key={skeletonKey} />
          ))
        : groups.map((g) => (
            <CertGroup
              key={g.id}
              title={g.course_name}
              courseUrl={g.course_detail_url}
              items={g.curriculums || []}
              comingSoon={g.coming_soon}
              ariaLabel={g.course_name}
            />
          ))}
    </SectionShell>
  );
}

export default React.memo(SertifikasiCarousel);
